import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Button, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter,
    ModalHeader, Row,
} from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import Loader from "../../shared/Loader";
import { errorToast, successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Tile, Badge, TablePagination } from "../reporting/ReportingShared";
import {
    getMembership, getMembershipAudits, getMembershipUsage, getMembershipTypes,
    getMembershipGroups,
    setMembershipStatus, setMembershipOverrides, rotateMembershipQr, addMembershipNote,
    assignMembership,
} from "../../utils/membershipApi";
import { StatusBadge } from "./membership-list";
import { AuditEntry } from "./ConfigAuditLog";
import {
    SETTABLE_STATUSES, STATUS_LABELS,
    AUDIT_ACTION_LABELS, LEDGER_EVENT_LABELS, formatDiscount, formatCap,
} from "./membershipConstants";

/**
 * One member's whole record: their card, what they may spend, what they have spent, and
 * every administrative change anyone has ever made to it.
 *
 * The design principle here is the one from the plan: all three layers must be reachable
 * from the UI. The tier's own config is a link away; the per-member overrides and the
 * eligibility they inherit are shown side by side, so an admin can see at a glance
 * whether a limit comes from the tier or from this person.
 *
 * Two things are deliberately harder than they could be:
 *
 * There is no delete. A card is stopped with a status change, which keeps the row, the
 * card number and the history. "Revoke now, re-issue in March" therefore returns the same
 * card number and the same QR, and the ledger's attribution of past spend stays intact.
 *
 * Nothing on this page saves silently. Status changes and override edits both go through
 * a dialogue that asks for a reason, because the API writes a before/after audit row in
 * the same transaction and an unexplained change is worse than no change at all — it
 * looks like a bug to whoever finds it six months later.
 */

const labelStyle = { fontWeight: 500, fontSize: 14 };
const hintStyle = { fontSize: 12, color: C.textMed, marginTop: 4, display: "block" };

const ghostButton = {
    padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : "—");
const money = (n) => `SAR ${Number(n || 0).toFixed(2)}`;

const toNullableNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

/** A row of the audit diff: "Max orders per day: 3 → 5". */
const MemberProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [member, setMember] = useState(null);
    const [types, setTypes] = useState([]);
    const [groups, setGroups] = useState([]);
    const [audits, setAudits] = useState({ rows: [], total: 0, totalPages: 0 });
    const [usage, setUsage] = useState({ rows: [], total: 0, totals: { discountGross: 0 } });
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Both logs page on the server, independently of each other and of the record itself.
     *
     * They are append-only and only grow — a long-standing member accumulates a discount
     * event per order and an audit row per admin action — so neither can be fetched whole.
     * Kept out of `load()` so paging one of them does not refetch the member, its tier
     * list and the other log as well.
     */
    const [usagePage, setUsagePage] = useState(1);
    const [usageLimit, setUsageLimit] = useState(20);
    const [auditPage, setAuditPage] = useState(1);
    const [auditLimit, setAuditLimit] = useState(20);

    const [statusDialog, setStatusDialog] = useState(null);
    const [overrideDialog, setOverrideDialog] = useState(null);
    const [tierDialog, setTierDialog] = useState(null);
    const [noteDraft, setNoteDraft] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        const data = await getMembership(id);
        if (!data) { setIsLoading(false); navigate("/membership/members"); return; }
        setMember(data);
        setNoteDraft(data.internalNote || "");

        const [typeRows, groupRows] = await Promise.all([
            getMembershipTypes({ merchantId: data.merchantId }),
            // Retired companies included on purpose: a member may still be sitting in one,
            // and the picker has to be able to show where they currently are.
            getMembershipGroups({ merchantId: data.merchantId }),
        ]);
        setTypes(typeRows || []);
        setGroups(groupRows || []);
        setIsLoading(false);
    }, [id, navigate]);

    /** The ledger, paged. Needs the merchant id, so it waits for the record. */
    const loadUsage = useCallback(async () => {
        if (!member?.merchantId) return;
        setUsage(await getMembershipUsage({
            merchantId: member.merchantId,
            membershipId: id,
            page: usagePage,
            limit: usageLimit,
        }));
    }, [id, member?.merchantId, usagePage, usageLimit]);

    const loadAudits = useCallback(async () => {
        setAudits(await getMembershipAudits(id, { page: auditPage, limit: auditLimit }));
    }, [id, auditPage, auditLimit]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadUsage(); }, [loadUsage]);
    useEffect(() => { loadAudits(); }, [loadAudits]);

    /**
     * After a mutation, reload the record and both logs — every action here writes an
     * audit row, and a status change can move the ledger too.
     *
     * Paging is reset to the first page on purpose: the new entry is the newest, so it is
     * on page 1, and leaving the admin on page 4 after acting would look like nothing
     * happened.
     */
    const reloadAll = useCallback(() => {
        load();
        setUsagePage(1);
        setAuditPage(1);
        loadUsage();
        loadAudits();
    }, [load, loadUsage, loadAudits]);

    const name = useMemo(
        () => [member?.user?.firstName, member?.user?.lastName].filter(Boolean).join(" ") || "—",
        [member]
    );

    const type = member?.membershipType;
    const caps = member?.effectiveCaps || {};

    /** Whether a cap on this member came from an override or from the tier. */
    const capSource = (field) =>
        member?.[field] !== null && member?.[field] !== undefined ? "member" : "tier";

    /**
     * The company this member spends from, if any.
     *
     * `groupId` null means an individual member, which is the majority case — so this is
     * routinely null and the company rows are simply absent rather than shown empty.
     */
    const memberGroup = member?.groupId
        ? groups.find((g) => Number(g.id) === Number(member.groupId)) || null
        : null;

    if (isLoading || !member) return <Loader />;

    const usable = member.usability?.usable;

    // ── Handlers ──────────────────────────────────────────────────────────────────────

    const saveStatus = async () => {
        const { status, reason, customerMessage } = statusDialog;
        if (!status) { errorToast("Choose a status."); return; }
        if (["suspended", "revoked"].includes(status) && !reason?.trim()) {
            errorToast("A reason is required when stopping a card.");
            return;
        }
        const saved = await setMembershipStatus(id, {
            status,
            reason: reason || null,
            customerMessage: customerMessage || null,
        });
        if (saved) {
            successToast(`Status changed to ${STATUS_LABELS[status] || status}`);
            setStatusDialog(null);
            reloadAll();
        }
    };

    const saveOverrides = async () => {
        const {
            maxOrderValue, maxDailyOrders, maxMonthlyOrders,
            employeeId, groupId, reason,
        } = overrideDialog;
        const saved = await setMembershipOverrides(id, {
            maxOrderValue: toNullableNumber(maxOrderValue),
            maxDailyOrders: toNullableNumber(maxDailyOrders),
            maxMonthlyOrders: toNullableNumber(maxMonthlyOrders),
            employeeId: employeeId || null,
            // "" is the "No company" option, and it has to reach the API as null — which
            // is a real instruction meaning "make them an individual member".
            groupId: toNullableNumber(groupId),
            reason: reason || null,
        });
        if (saved) {
            successToast("Limits updated");
            setOverrideDialog(null);
            reloadAll();
        }
    };

    const saveTier = async () => {
        const { membershipTypeId, reason } = tierDialog;
        if (!membershipTypeId) { errorToast("Choose a tier."); return; }
        // Re-assigning onto the same row: one membership per (customer, merchant) is a
        // unique index, so this changes the tier and keeps the card number and QR.
        const saved = await assignMembership({
            merchantId: member.merchantId,
            userId: member.userId,
            membershipTypeId: Number(membershipTypeId),
            reason: reason || null,
        });
        if (saved) {
            successToast("Tier changed");
            setTierDialog(null);
            reloadAll();
        }
    };

    const doQr = async (action) => {
        const prompts = {
            disable: "Disable this card's QR code? The member can still order in the app, but the code stops working at the counter.",
            enable: "Re-enable this card's QR code?",
            reissue: "Re-issue the QR code? The printed or screenshotted code the member has now stops working. Use this if the code has been shared.",
        };
        if (!window.confirm(prompts[action])) return;
        const saved = await rotateMembershipQr(id, { action });
        if (saved) { successToast("QR updated"); load(); }
    };

    const saveNote = async () => {
        setSavingNote(true);
        const saved = await addMembershipNote(id, noteDraft);
        setSavingNote(false);
        if (saved) { successToast("Note saved"); load(); }
    };

    return (
        <Fragment>
            <Breadcrumb title="Member" parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* ── Header ──────────────────────────────────────────────── */}
                    <RCard>
                        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                                <div style={{
                                    width: 6, alignSelf: "stretch", borderRadius: 3,
                                    background: type?.cardColour || C.textLight,
                                }} />
                                <div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{name}</div>
                                    <div style={{ fontSize: 13, color: C.textMed, marginTop: 2 }}>
                                        {member.user?.phoneNo || "—"}
                                        {member.user?.email ? ` · ${member.user.email}` : ""}
                                        {member.employeeId ? ` · Employee #${member.employeeId}` : ""}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                                        <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: C.text }}>
                                            {member.membershipRef}
                                        </span>
                                        <StatusBadge status={member.status} />
                                        <Badge color={C.blue} bg={C.blueLight}>
                                            {type?.name || "No tier"} · {formatDiscount(type?.discountType, type?.discountValue)}
                                        </Badge>
                                        {member.qrStatus === "disabled" && (
                                            <Badge color={C.coral} bg={C.coralLight}>QR disabled</Badge>
                                        )}
                                        {/* No "Photo not verified" badge any more — it read as a warning
                                            about something blocked, and nothing was ever blocked by it.
                                            A member awaiting sign-off shows as Pending, which is true. */}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                                <button style={ghostButton} onClick={() => setTierDialog({ membershipTypeId: String(type?.id || ""), reason: "" })}>
                                    Change tier
                                </button>
                                <button
                                    style={ghostButton}
                                    onClick={() => setStatusDialog({ status: member.status, reason: "", customerMessage: member.customerMessage || "" })}
                                >
                                    Change status
                                </button>
                                <button style={ghostButton} onClick={() => doQr(member.qrStatus === "disabled" ? "enable" : "disable")}>
                                    {member.qrStatus === "disabled" ? "Enable QR" : "Disable QR"}
                                </button>
                                <button style={ghostButton} onClick={() => doQr("reissue")}>Re-issue QR</button>
                            </div>
                        </div>

                        {/*
                          Why the card is not working, when it is not. This is the single most
                          useful thing on the page for support: the same reason code the app
                          shows the customer, spelled out here.
                        */}
                        {!usable && (
                            <div style={{
                                marginTop: 16, padding: "12px 16px", borderRadius: 8,
                                background: C.coralLight, color: C.text, fontSize: 13,
                            }}>
                                <strong>This card is giving no discount.</strong>{" "}
                                {member.usability?.reason === "membership_expired" && `It expired on ${fmtDate(member.expiryDate)}.`}
                                {/* No `photo_unverified` branch any more: the API stopped
                                    gating the discount on a photo, so it no longer sends
                                    that detail and this could only ever have been reached
                                    by a hand-made fixture. `status` is the one gate. */}
                                {member.usability?.reason === "membership_inactive"
                                    && `Its status is ${STATUS_LABELS[member.status] || member.status}.`}
                                {member.usability?.reason === "type_inactive" && "Its tier has been deactivated."}
                                {member.usability?.reason === "type_window" && "Its tier is outside its effective dates."}
                                {member.usability?.reason === "program_disabled" && "Membership is switched off for this merchant."}
                                {member.suspensionReason && (
                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        <strong>Internal reason:</strong> {member.suspensionReason}
                                    </div>
                                )}
                                {member.revocationReason && (
                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        <strong>Internal reason:</strong> {member.revocationReason}
                                    </div>
                                )}
                                {member.customerMessage && (
                                    <div style={{ marginTop: 6, fontSize: 12 }}>
                                        <strong>Shown to the customer:</strong> “{member.customerMessage}”
                                    </div>
                                )}
                            </div>
                        )}
                    </RCard>

                    {/* ── Tiles ───────────────────────────────────────────────── */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, margin: "20px 0" }}>
                        <Tile label="Lifetime Saved" value={money(member.lifetimeDiscount)} sub="Net of returns" icon="💰" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Today" value={`${member.usage?.daily?.orders ?? 0} orders`} sub={money(member.usage?.daily?.value)} icon="📅" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                        <Tile label="This Month" value={`${member.usage?.monthly?.orders ?? 0} orders`} sub={money(member.usage?.monthly?.value)} icon="🗓️" gradient={`linear-gradient(135deg, ${C.purple}, #C89BFF)`} delay={160} />
                        <Tile label="Discount Events" value={usage.total || 0} sub="Applications and reversals" icon="🧾" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={240} />
                    </div>

                    <Row>
                        {/* ── Card details ────────────────────────────────────── */}
                        <Col lg="6">
                            <RCard title="🎫 Card">
                                <table style={{ ...tbl.table, fontSize: 13 }}>
                                    <tbody>
                                        {[
                                            ["Card number", <span style={{ fontFamily: "monospace" }}>{member.membershipRef}</span>],
                                            ["Tier", type ? (
                                                <span
                                                    style={{ color: C.coral, cursor: "pointer", fontWeight: 600 }}
                                                    onClick={() => navigate(`/membership/types/edit/${type.id}`)}
                                                >
                                                    {type.name} ↗
                                                </span>
                                            ) : "—"],
                                            ["Issued", fmtDate(member.issuedDate)],
                                            ["Expires", member.expiryDate ? fmtDate(member.expiryDate) : "Never"],
                                            ["QR", member.qrStatus === "active" ? "Active" : "Disabled"],
                                            ["Approved by", member.approvedBy ? `${member.approvedBy} · ${fmtDate(member.approvedDate)}` : "—"],
                                            ["Assignment reason", member.assignmentReason || "—"],
                                            member.suspendedDate && ["Suspended", `${fmtDate(member.suspendedDate)} — ${member.suspensionReason || "no reason recorded"}`],
                                            member.revokedDate && ["Revoked", `${fmtDate(member.revokedDate)} — ${member.revocationReason || "no reason recorded"}`],
                                        ].filter(Boolean).map(([label, value]) => (
                                            <tr key={label}>
                                                <td style={{ ...tbl.td, color: C.textMed, width: "40%" }}>{label}</td>
                                                <td style={{ ...tbl.td, color: C.text }}>{value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </RCard>
                        </Col>

                        {/* ── Limits ──────────────────────────────────────────── */}
                        <Col lg="6">
                            <RCard
                                title={
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                        <span>🛡️ Limits in force</span>
                                        <button
                                            style={ghostButton}
                                            onClick={() => setOverrideDialog({
                                                maxOrderValue: member.maxOrderValue ?? "",
                                                maxDailyOrders: member.maxDailyOrders ?? "",
                                                maxMonthlyOrders: member.maxMonthlyOrders ?? "",
                                                employeeId: member.employeeId || "",
                                                groupId: member.groupId ?? "",
                                                reason: "",
                                            })}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                }
                            >
                                <table style={{ ...tbl.table, fontSize: 13 }}>
                                    <tbody>
                                        {[
                                            ["Max order value", formatCap(caps.maxOrderValue, " SAR"), capSource("maxOrderValue")],
                                            ["Orders per day", formatCap(caps.maxDailyOrders), capSource("maxDailyOrders")],
                                            ["Orders per month", formatCap(caps.maxMonthlyOrders), capSource("maxMonthlyOrders")],
                                            // This one has no per-member override on purpose — it is a budget
                                            // the tier owns, not a permission granted to a person.
                                            ["Daily discount budget", formatCap(caps.maxDailyDiscountValue, " SAR"), "tier"],
                                            // The company's shared pot, listed only when there is one. It is a
                                            // THIRD source of truth alongside the tier and this member, and the
                                            // discount is trimmed to whichever of them is nearest to spent —
                                            // so a member whose own budget is untouched can still get nothing.
                                            ...(memberGroup ? [
                                                [
                                                    `Company daily budget · ${memberGroup.name}`,
                                                    formatCap(memberGroup.maxDailyDiscountValue, " SAR"),
                                                    "group",
                                                ],
                                                [
                                                    `Company monthly budget · ${memberGroup.name}`,
                                                    formatCap(memberGroup.maxMonthlyDiscountValue, " SAR"),
                                                    "group",
                                                ],
                                            ] : []),
                                        ].map(([label, value, source]) => (
                                            <tr key={label}>
                                                <td style={{ ...tbl.td, color: C.textMed, width: "45%" }}>{label}</td>
                                                <td style={{ ...tbl.td, color: C.text, fontWeight: 500 }}>{value}</td>
                                                <td style={{ ...tbl.td, textAlign: "right" }}>
                                                    {source === "member" && <Badge color={C.purple} bg={C.purpleLight}>This member</Badge>}
                                                    {source === "group" && <Badge color={C.blue} bg={C.blueLight}>Shared</Badge>}
                                                    {source === "tier" && <span style={{ fontSize: 11, color: C.textLight }}>From tier</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{ ...hintStyle, marginTop: 12 }}>
                                    Limits are counted in the merchant's timezone and are net of returns — a fully
                                    returned order releases the slot it used.
                                </div>
                            </RCard>
                        </Col>
                    </Row>

                    {/* ── Internal note ───────────────────────────────────────── */}
                    <RCard title="🔒 Internal note">
                        <Input
                            type="textarea" rows="3"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            placeholder="Anything the next admin should know. Never shown to the customer."
                        />
                        <div style={{ marginTop: 10 }}>
                            <Button color="primary" size="sm" onClick={saveNote} disabled={savingNote || noteDraft === (member.internalNote || "")}>
                                {savingNote ? "Saving…" : "Save note"}
                            </Button>
                            <span style={{ ...hintStyle, display: "inline", marginLeft: 12 }}>
                                To write something the <strong>customer</strong> sees, use Change status — its
                                message field is the one the app displays.
                            </span>
                        </div>
                    </RCard>

                    {/* ── Usage ledger ────────────────────────────────────────── */}
                    <RCard title={`🧾 Discount history · net ${money(usage.totals?.discountGross)}`}>
                        {(usage.rows || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                                This member has not used their discount yet.
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>When</th>
                                            <th style={tbl.th}>Event</th>
                                            <th style={tbl.th}>Order</th>
                                            <th style={tbl.th}>Order total</th>
                                            <th style={tbl.th}>Tier at the time</th>
                                            <th style={tbl.th}>Discount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usage.rows.map((r) => {
                                            const gross = Number(r.discountGross);
                                            const isReversal = gross < 0;
                                            return (
                                                <tr key={r.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{fmtDateTime(r.createdAt)}</td>
                                                    <td style={tbl.td}>
                                                        {isReversal
                                                            ? <Badge color={C.coral} bg={C.coralLight}>{LEDGER_EVENT_LABELS[r.eventType] || r.eventType}</Badge>
                                                            : <Badge color={C.green} bg={C.greenLight}>{LEDGER_EVENT_LABELS[r.eventType] || r.eventType}</Badge>}
                                                    </td>
                                                    <td style={tbl.td}>{r.order?.merchantOrderId || `#${r.orderId}`}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed }}>{r.order?.grandTotal != null ? money(r.order.grandTotal) : "—"}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {/* The ledger row carries its own tier, so an upgrade never
                                                            re-attributes what was spent before it. */}
                                                        {r.membershipType?.name || "—"}
                                                        {r.meta?.discountValue != null && ` · ${formatDiscount(r.meta.discountType, r.meta.discountValue)}`}
                                                    </td>
                                                    <td style={{ ...tbl.td, fontWeight: 600, color: isReversal ? C.coral : C.text }}>
                                                        {money(gross)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {/* Server-side, so a member with years of orders does not
                                    arrive as one enormous response. The heading's total is
                                    the whole ledger, not this page. */}
                                <TablePagination
                                    total={usage.total || 0}
                                    page={usagePage}
                                    limit={usageLimit}
                                    onPageChange={setUsagePage}
                                    onLimitChange={(l) => { setUsageLimit(l); setUsagePage(1); }}
                                />
                            </div>
                        )}
                    </RCard>

                    {/* ── Audit history ───────────────────────────────────────── */}
                    <RCard title={`📜 Administrative history · ${audits.total || 0} change${audits.total === 1 ? "" : "s"}`}>
                        {(audits.rows || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 30, color: C.textMed }}>
                                {auditPage > 1 ? "Nothing further back." : "No changes recorded."}
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Same entry component as the tier and company histories, so the
                                    three logs cannot drift into describing a change three ways.
                                    The status transition goes on the title line, which is the one
                                    thing a member's history has and a tier's does not. */}
                                {audits.rows.map((a) => (
                                    <AuditEntry
                                        key={a.id}
                                        title={
                                            <>
                                                {AUDIT_ACTION_LABELS[a.action] || a.action}
                                                {a.previousStatus && a.newStatus && (
                                                    <span style={{ fontWeight: 400, color: C.textMed }}>
                                                        {" "}· {STATUS_LABELS[a.previousStatus] || a.previousStatus} → {STATUS_LABELS[a.newStatus] || a.newStatus}
                                                    </span>
                                                )}
                                            </>
                                        }
                                        createdAt={a.createdAt}
                                        adminEmail={a.adminEmail}
                                        reason={a.reason}
                                        previousValues={a.previousValues}
                                        newValues={a.newValues}
                                    />
                                ))}
                                {/* Append-only, so this grows for the life of the card —
                                    it used to arrive as one array capped at 500, which
                                    silently hid the oldest entries. */}
                                <TablePagination
                                    total={audits.total || 0}
                                    page={auditPage}
                                    limit={auditLimit}
                                    onPageChange={setAuditPage}
                                    onLimitChange={(l) => { setAuditLimit(l); setAuditPage(1); }}
                                />
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>

            {/* ── Status dialogue ─────────────────────────────────────────────── */}
            {statusDialog && (
                <Modal isOpen toggle={() => setStatusDialog(null)} centered>
                    <ModalHeader toggle={() => setStatusDialog(null)}>Change status</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label className="mb-2" style={labelStyle}>New status</Label>
                            <Input
                                type="select"
                                value={statusDialog.status}
                                onChange={(e) => setStatusDialog((d) => ({ ...d, status: e.target.value }))}
                            >
                                {SETTABLE_STATUSES.map((s) => (
                                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                ))}
                            </Input>
                            {/*
                              "Expired" is missing from the list on purpose: it is reached by the
                              calendar, not by a decision, and it records no reason. To end a card
                              early, revoke it.
                            */}
                            <span style={hintStyle}>
                                To end a card before its expiry date, revoke it — that records who did it and why.
                            </span>
                        </FormGroup>
                        <FormGroup>
                            <Label className="mb-2" style={labelStyle}>
                                Internal reason
                                {["suspended", "revoked"].includes(statusDialog.status) && <span style={{ color: C.coral }}> *</span>}
                            </Label>
                            <Input
                                type="textarea" rows="2"
                                value={statusDialog.reason}
                                onChange={(e) => setStatusDialog((d) => ({ ...d, reason: e.target.value }))}
                                placeholder="What happened. Admin eyes only."
                            />
                            <span style={hintStyle}>Never leaves the panel — the app never receives this field.</span>
                        </FormGroup>
                        <FormGroup>
                            <Label className="mb-2" style={labelStyle}>Message for the customer</Label>
                            <Input
                                type="textarea" rows="2"
                                value={statusDialog.customerMessage}
                                onChange={(e) => setStatusDialog((d) => ({ ...d, customerMessage: e.target.value }))}
                                placeholder="e.g. Your card is on hold. Please contact HR."
                            />
                            <span style={hintStyle}>
                                <strong>Shown in the pickup app.</strong> A member who sees a stopped card with no
                                explanation calls support; one who sees a reason does not. Leave blank and the app
                                shows only its own generic wording.
                            </span>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setStatusDialog(null)}>Cancel</Button>
                        <Button color="primary" onClick={saveStatus}>Save</Button>
                    </ModalFooter>
                </Modal>
            )}

            {/* ── Overrides dialogue ──────────────────────────────────────────── */}
            {overrideDialog && (
                <Modal isOpen toggle={() => setOverrideDialog(null)} centered>
                    <ModalHeader toggle={() => setOverrideDialog(null)}>Limits for this member</ModalHeader>
                    <ModalBody>
                        <div style={{ padding: 10, borderRadius: 8, background: C.bg, marginBottom: 16, fontSize: 12, color: C.textMed }}>
                            Blank means "use the tier's limit". A number here lifts this member off the
                            tier's published terms, so the change is recorded with what it replaced.
                        </div>
                        <Form>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className="mb-2" style={labelStyle}>Max order value (SAR)</Label>
                                        <Input
                                            type="number" step="0.01"
                                            value={overrideDialog.maxOrderValue}
                                            onChange={(e) => setOverrideDialog((d) => ({ ...d, maxOrderValue: e.target.value }))}
                                            placeholder={formatCap(type?.maxOrderValue)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className="mb-2" style={labelStyle}>Orders per day</Label>
                                        <Input
                                            type="number"
                                            value={overrideDialog.maxDailyOrders}
                                            onChange={(e) => setOverrideDialog((d) => ({ ...d, maxDailyOrders: e.target.value }))}
                                            placeholder={formatCap(type?.maxDailyOrders)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className="mb-2" style={labelStyle}>Orders per month</Label>
                                        <Input
                                            type="number"
                                            value={overrideDialog.maxMonthlyOrders}
                                            onChange={(e) => setOverrideDialog((d) => ({ ...d, maxMonthlyOrders: e.target.value }))}
                                            placeholder={formatCap(type?.maxMonthlyOrders)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className="mb-2" style={labelStyle}>Employee ID</Label>
                                        <Input
                                            value={overrideDialog.employeeId}
                                            onChange={(e) => setOverrideDialog((d) => ({ ...d, employeeId: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            {/* "Photo verification" used to be a field here. It is gone, and the API no
                                longer accepts it: nothing in the pricing path ever read it, so setting it
                                to Verified asserted a check nobody had performed — the same fault the photo
                                gate itself had. A field that looks like a control but changes nothing is
                                worse than no field, because an admin sets it and expects an effect.
                                `status` is the one real gate, and an admin opens it deliberately. */}

                            <FormGroup>
                                <Label className="mb-2" style={labelStyle}>Company</Label>
                                <Input
                                    type="select"
                                    value={overrideDialog.groupId}
                                    onChange={(e) => setOverrideDialog((d) => ({ ...d, groupId: e.target.value }))}
                                >
                                    {/* No company is the NORMAL case, so it is the first option and it is
                                        named plainly — an individual member, not a missing setting. */}
                                    <option value="">No company (individual member)</option>
                                    {groups.map((g) => (
                                        <option key={g.id} value={g.id}>
                                            {g.name}{g.isActive ? "" : " (retired)"}
                                        </option>
                                    ))}
                                </Input>
                                <span style={hintStyle}>
                                    A company's budget is shared across its members. Moving this member does
                                    not move their past spend — that stays with the company that funded it.
                                </span>
                            </FormGroup>
                            <FormGroup>
                                <Label className="mb-2" style={labelStyle}>Reason</Label>
                                <Input
                                    value={overrideDialog.reason}
                                    onChange={(e) => setOverrideDialog((d) => ({ ...d, reason: e.target.value }))}
                                    placeholder="Why this member differs from their tier"
                                />
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setOverrideDialog(null)}>Cancel</Button>
                        <Button color="primary" onClick={saveOverrides}>Save</Button>
                    </ModalFooter>
                </Modal>
            )}

            {/* ── Tier dialogue ───────────────────────────────────────────────── */}
            {tierDialog && (
                <Modal isOpen toggle={() => setTierDialog(null)} centered>
                    <ModalHeader toggle={() => setTierDialog(null)}>Change tier</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label className="mb-2" style={labelStyle}>Tier</Label>
                            <Input
                                type="select"
                                value={tierDialog.membershipTypeId}
                                onChange={(e) => setTierDialog((d) => ({ ...d, membershipTypeId: e.target.value }))}
                            >
                                <option value="">Choose a tier…</option>
                                {types.filter((t) => t.isActive || String(t.id) === String(type?.id)).map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name} — {formatDiscount(t.discountType, t.discountValue)}{t.isActive ? "" : " (inactive)"}
                                    </option>
                                ))}
                            </Input>
                            <span style={hintStyle}>
                                The card number and QR stay the same. Past orders keep the tier they were placed
                                under, and so does the discount history above.
                            </span>
                        </FormGroup>
                        <FormGroup>
                            <Label className="mb-2" style={labelStyle}>Reason</Label>
                            <Input
                                value={tierDialog.reason}
                                onChange={(e) => setTierDialog((d) => ({ ...d, reason: e.target.value }))}
                                placeholder="e.g. Promoted to shift leader"
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setTierDialog(null)}>Cancel</Button>
                        <Button color="primary" onClick={saveTier}>Save</Button>
                    </ModalFooter>
                </Modal>
            )}
        </Fragment>
    );
};

export default MemberProfile;
